import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useMemo, useState } from 'react';

import InviteMerchantForm from '@/components/forms/store/InviteMerchantForm';
import ConfirmDeleteModal from '@/components/modal/common/ConfirmDeleteModal';
import LoadingSkeletonModalTable from '@/components/modal/prompt/LoadingSkeletonModalTable';
import StoreMerchantsTable from '@/components/table/store/StoreMerchantsTable';
import Button from '@/components/ui/button';
import { formatDateTime } from '@/components/utils/utils';
import { useModal } from '@/hooks/useModal';
import { useUser } from '@/hooks/useUser';
import { useUserStore } from '@/hooks/useUserStore';
import { IUser } from '@/interfaces/api/IUser';
import { UserRole } from '@/interfaces/api/user/UserRole.enum';
import { haggleApiService } from '@/services/haggle.service';
import { notificationService } from '@/services/notification.service';

const StoreMerchantsDashboard = () => {
	const [isLoading, setIsLoading] = useState(false);
	const [userId, setUserId] = useState<number | undefined>(undefined);
	const [showInviteForm, setShowInviteForm] = useState(false);
	const { user } = useUser();
	const {
		store,
		refetch: refetchStore,
		isLoading: storeIsLoading,
	} = useUserStore();

	const oldestUser = store?.users.reduce((oldest, current) => {
		return new Date(current.createdAt) < new Date(oldest.createdAt)
			? current
			: oldest;
	}, store?.users[0]);

	const handleRemoveUserFromStore = async (id: number) => {
		setIsLoading(true);
		try {
			await haggleApiService.removeUserFromStore(id);
			refetchStore();
			notificationService.success('User removed from the store successfully');
		} catch (error) {
			if (error instanceof Error) {
				notificationService.error(error.message);
			} else {
				notificationService.error(
					`Unknown error when trying to remove user from store: ${error}`,
				);
			}
		} finally {
			setIsLoading(false);
		}
	};

	const columnHelper = createColumnHelper<IUser>();

	const columns = useMemo(() => {
		const commonColumns = [
			columnHelper.accessor('id', {
				cell: (info) => <div className="w-[50px]">{info.getValue()}</div>,
				header: () => <div className="w-[50px]">ID</div>,
			}) as ColumnDef<IUser>,
			columnHelper.accessor('username', {
				cell: (info) => <div className="w-[250px]">{info.getValue()}</div>,
				header: () => <div className="w-[250px]">EMAIL</div>,
			}) as ColumnDef<IUser>,
			columnHelper.accessor('createdAt', {
				cell: (info) => (
					<div className="w-[200px]">{formatDateTime(info.getValue())}</div>
				),
				header: () => <div className="w-[200px]">CREATED AT</div>,
			}) as ColumnDef<IUser>,
			{
				id: 'remove',
				accessorFn: (row) => row,
				cell: ({ getValue }) => {
					const rowUser = getValue() as IUser;
					return (
						<div className="w-[100px]">
							{rowUser.id !== oldestUser?.id && (
								<button
									className="font-bold text-red-500 hover:underline"
									onClick={() => handleOpenDeleteModal(rowUser.id)}
									data-test={`remove-user-button-${rowUser.id}`}
								>
									{isLoading ? 'Removing...' : 'Remove'}
								</button>
							)}
						</div>
					);
				},
				header: () => <div className="w-[100px]">REMOVE</div>,
			} as ColumnDef<IUser>,
		];

		return commonColumns;
	}, [oldestUser]);

	const handleOpenDeleteModal = (id: number) => {
		setUserId(id);
		openConfirmRemoveUserModal();
	};

	const {
		modal: confirmRemoveUserModal,
		openModal: openConfirmRemoveUserModal,
		closeModal: closeConfirmRemoveUserModal,
	} = useModal({
		children: (
			<ConfirmDeleteModal
				submit={handleRemoveUserFromStore}
				closeModal={() => closeConfirmRemoveUserModal()}
				message={`Are you sure you want to remove the with ID ${userId} from your store?`}
				elementId={userId}
			/>
		),
		closeOnOutClick: false,
		modalBoxClassName: 'max-w-[600px]',
	});

	return (
		<div
			className="flex flex-col justify-start items-start w-full"
			data-test="store-merchants-dashboard"
		>
			{/* Add invite button */}
			{!user?.roles.includes(UserRole.Admin) && store && !storeIsLoading && (
				<div className="w-full mb-4 flex justify-start">
					<Button
						color="white"
						className="border !border-grey-30"
						shape="rounded"
						transition="none"
						onClick={() => setShowInviteForm(!showInviteForm)}
						data-test="invite-merchant-modal-button"
					>
						{showInviteForm ? 'Hide Invite Form' : 'Invite User to the Store'}
					</Button>
				</div>
			)}

			{/* Show invite form */}
			{showInviteForm && (
				<div className="w-full sm:w-3/4 md:w-2/3 lg:w-2/6 flex justify-start mb-6 bg-white rounded-md ">
					<InviteMerchantForm refetchStore={refetchStore} />
				</div>
			)}

			{storeIsLoading && <LoadingSkeletonModalTable />}

			{store && !storeIsLoading && (
				<StoreMerchantsTable users={store.users} columns={columns} />
			)}

			{confirmRemoveUserModal}
		</div>
	);
};
export default StoreMerchantsDashboard;
